// src/components/operationsAssociate/BulkItemUploadModal.jsx
import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net";

const toCamelCase = (str) => {
    return str
        .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : "")
        .replace(/^./, (str) => str.toLowerCase());
};

export default function BulkItemUploadModal({ closeModal, onAssetsUploaded }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
     const [uploadedFile, setUploadedFile] = useState(null);

    const processImportedData = (data) => {
        if (!data || data.length === 0) {
            setError("The file is empty or has no data.");
            setItems([]);
            return;
        }

        const newItems = data.map((item) => {
            let itemObject = {};
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    const normalizedKey = toCamelCase(key.trim());
                    let value = item[key];
                    if (normalizedKey === "inStock") {
                        value = parseFloat(value) || 0;
                    }
                    itemObject[normalizedKey] = value;
                }
            }
            return itemObject;
        }).filter(item => item.name); // Filter out items without a name

        if (newItems.length > 0) {
            setItems(newItems);
            setSuccess("File imported successfully. Review the data below before saving.");
        } else {
            setError("The file contains no valid item data. Please check the 'Name' column.");
            setItems([]);
        }
    };

    const handleFileUpload = (e) => {
  const file = e.target.files[0];
  setError("");
  setSuccess("");

  if (file) {
    setUploadedFile(file); // <-- store file for display
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (fileExtension === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => processImportedData(results.data),
        error: (err) => setError("Failed to parse CSV file."),
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        processImportedData(jsonData);
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError("Unsupported file format. Please upload a .csv or .xlsx file.");
    }
  }
};

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (items.length === 0) {
            return setError("No items to save. Please upload a file first.");
        }

        setLoading(true);
        const token = localStorage.getItem("token");

        try {
            await axios.post(`${API_URL}/api/inventory/bulk`, items, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            toast.success(`${items.length} items added successfully!`);
            onAssetsUploaded();
            closeModal();
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err.message || "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage}`);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl relative space-y-6 max-h-[90vh] overflow-y-auto">
                <button type="button" onClick={closeModal} className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold">&times;</button>
                <h2 className="text-2xl font-semibold text-orange-600">Bulk Upload Inventory</h2>

                {error && <div className="text-red-500 text-sm">{error}</div>}
                {success && <div className="text-green-500 text-sm">{success}</div>}

                <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-b pb-4">
                    <input type="file" accept=".csv, .xlsx" onChange={handleFileUpload} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" />
                </div>

                <div className="overflow-x-auto border rounded">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3 text-left text-xs font-medium text-gray-500">Name</th>
                                <th className="p-3 text-left text-xs font-medium text-gray-500">SKU</th>
                                <th className="p-3 text-left text-xs font-medium text-gray-500">Category</th>
                                <th className="p-3 text-left text-xs font-medium text-gray-500">In Stock</th>
                                <th className="p-3 text-left text-xs font-medium text-gray-500">Location</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.length > 0 ? items.map((item, index) => (
                                <tr key={index}>
                                    <td className="p-3">{item.name}</td>
                                    <td className="p-3">{item.sku}</td>
                                    <td className="p-3">{item.category}</td>
                                    <td className="p-3">{item.inStock}</td>
                                    <td className="p-3">{item.location}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="p-4 text-center text-gray-500">Upload a file or add items manually.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-4 mt-4">
                    <button type="button" onClick={closeModal} className="px-4 py-2 border rounded hover:bg-gray-100" disabled={loading}>Cancel</button>
                    <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700" disabled={loading}>
                        {loading ? "Saving..." : "Save All Items"}
                    </button>
                </div>
            </form>
        </div>
    );
}